import { ref, watch } from 'vue'
import navigationConfig from '@/components/nav/config.json'

const navigation = ref({})

let initialized = false
let storeRef = null
let stopPermissionsWatch = null
let currentRouteRef = null

const deepClone = (obj) => structuredClone(obj)

const normalizePath = (path) => {
  if (typeof path !== 'string') return ''
  const trimmed = path.trim()
  if (!trimmed) return ''
  const noTrailing = trimmed.replace(/\/+$/, '')
  return noTrailing || '/'
}

const pathMatches = (itemTo, routePath) => {
  const toPath = normalizePath(itemTo)
  const current = normalizePath(routePath)
  if (!toPath || !current) return false
  return current === toPath || current.startsWith(`${toPath}/`)
}

const canSeeMenu = () => {
  if (!storeRef) return true
  return Boolean(storeRef.getters.canViewMenu || storeRef.getters.canAdminIndra)
}

const hasAnyPermission = (getterNames) => {
  if (!Array.isArray(getterNames) || getterNames.length === 0) return true
  if (!storeRef) return true
  return getterNames.some((getterName) => Boolean(storeRef.getters[getterName]))
}

const ensureStableId = (item, fallbackId) => {
  if (!item || typeof item !== 'object') return
  if (item.id === undefined || item.id === null || item.id === '') {
    item.id = fallbackId
  }
}

const processItem = (item, routePath, itemKeyPath) => {
  let hasActiveChild = false

  // `one-main-navigation` uses `menuItem.id` as both the Vue key and the internal menu index.
  // Children in our config don't always have `id`, which breaks open state & selection.
  ensureStableId(item, itemKeyPath)

  if (item.children && Object.keys(item.children).length) {
    const filteredChildren = {}

    for (const childKey of Object.keys(item.children)) {
      const childItem = item.children[childKey]

      // Generate a stable id for each child using the full path of keys.
      const childKeyPath = `${itemKeyPath}.${childKey}`

      if (!childItem.can || hasAnyPermission(childItem.can)) {
        const processedChild = processItem(childItem, routePath, childKeyPath)
        filteredChildren[childKey] = processedChild
        if (processedChild.active) hasActiveChild = true
      }
    }

    item.children = Object.keys(filteredChildren).length ? filteredChildren : undefined
  }

  const isSelfActive = Boolean(routePath && typeof item.to === 'string' && pathMatches(item.to, routePath))
  item.active = Boolean(isSelfActive || hasActiveChild)

  return item
}

const updateNavigation = (currentRoute) => {
  const nav = deepClone(navigationConfig)

  const routePath = currentRoute?.path || currentRoute?.fullPath?.split('?')?.[0] || null

  if (!canSeeMenu()) {
    navigation.value = {}
    return
  }

  const filteredNav = {}
  for (const key of Object.keys(nav)) {
    const item = nav[key]

    if (!item.can || hasAnyPermission(item.can)) {
      // Use existing numeric ids when present; otherwise fall back to a stable key path.
      const rootKeyPath = item?.id !== undefined && item?.id !== null && item?.id !== ''
        ? String(item.id)
        : key
      filteredNav[key] = processItem(item, routePath, rootKeyPath)
    }
  }

  navigation.value = filteredNav
}

export const useNavManager = (store) => {
  if (store) {
    storeRef = store

    if (!stopPermissionsWatch) {
      stopPermissionsWatch = watch(
        () => store.state.session,
        () => updateNavigation(currentRouteRef),
        { deep: true }
      )
    }
  }

  if (!initialized) {
    initialized = true
    updateNavigation(null)
  }

  const setCurrentItem = (route) => {
    currentRouteRef = route || null
    updateNavigation(currentRouteRef)
  }

  return {
    navigation,
    setCurrentItem
  }
}
